import {
  createSlice,
  PayloadAction
} from "@reduxjs/toolkit";
import {
  Method,
  StatusCode
} from "../../types/sharedTypes";
import { TabsConstants } from "../../constants/tabs.constants";
import { setIsSandbox } from "./authSlice";
import { SelectedVariable } from "../../types/variableType";
import { Environment } from "../../types/environmentTypes";

type ErrorModal = {
  title: string,
  description: string,
  primaryText: string
}

type MainStateType = {
  methods: Array<Method>,
  statusCodes: Array<StatusCode>,
  projectName?: string;
  login?: string;
  projectsSettings: {
    [login: string]: {
      [projectName: string]: {
        testrailEnable: boolean;
        selectedEnvironment: Environment,
        selectedTab: TabsConstants,
      }
    }
  },
  updateModal: {
    show: boolean,
    isDelete: boolean
  },
  showSpinner: boolean,
  selectedVariables: Array<SelectedVariable>,
  errorModal?: ErrorModal,
  openModalsId: Array<string>
  testCasesIsLaunched: boolean,
}

export const mainInitialState: MainStateType  = {
  methods: [],
  statusCodes: [],
  projectsSettings: {},
  updateModal: {
    show: false,
    isDelete: false
  },
  showSpinner: false,
  selectedVariables: [],
  openModalsId: [],
  testCasesIsLaunched: false
}

const mainSlice = createSlice({
  name: "mainSlice",
  initialState: mainInitialState,
  reducers: {
    setMethods: (state, action: PayloadAction<Array<Method>>) => {
      state.methods = action.payload;
    },
    setLogin: (state, action: PayloadAction<string>) => {
      state.login = action.payload;
      if (!state.projectsSettings[action.payload]) {
        state.projectsSettings[action.payload] = {
        };
      }
    },
    setProjectName: (state, action: PayloadAction<string>) => {
      state.projectName = action.payload;
      if (state.login && !state.projectsSettings[state.login][action.payload]) {
        state.projectsSettings[state.login][action.payload] = {
          selectedEnvironment: {
            id: 0,
            servers: [],
            testRunId: 0,
            name: "",
            defaultVariableValues: [],
            defaultHeaders: []
          },
          testrailEnable: true,
          selectedTab: 0,
        };
      }
    },
    setSelectedEnvironment: (state, action: PayloadAction<Environment>) => {
      if (state.login && state.projectName && state.projectsSettings[state.login][state.projectName]) {
        state.projectsSettings[state.login][state.projectName].selectedEnvironment = action.payload;
      }
    },
    setStatusCode: (state, action: PayloadAction<Array<StatusCode>>) => {
      state.statusCodes = action.payload;
    },
    setUpdateModal: (state, action: PayloadAction<{ show: boolean, isDelete: boolean }>) => {
      state.updateModal = action.payload;
    },
    setTestrailEnable: (state, action: PayloadAction<boolean>) => {
      if (state.login && state.projectName && state.projectsSettings[state.login][state.projectName]) {
        state.projectsSettings[state.login][state.projectName].testrailEnable = action.payload;
      }
    },
    setSelectedTab: (state, action: PayloadAction<TabsConstants>) => {
      if (state.login && state.projectName && state.projectsSettings[state.login][state.projectName]) {
        state.projectsSettings[state.login][state.projectName].selectedTab = action.payload;
      }
    },
    setShowSpinner: (state, action: PayloadAction<boolean>) => {
      state.showSpinner = action.payload;
    },
    setSelectedVariables: (state, action: PayloadAction<Array<SelectedVariable>>) => {
      state.selectedVariables = action.payload;
    },
    showErrorModal: (state, action: PayloadAction<ErrorModal>) => {
      state.errorModal = action.payload;
    },
    hideErrorModal: (state) => {
      state.errorModal = undefined;
    },
    addOpenModal: (state, action: PayloadAction<string>) => {
      const modalId = action.payload;
      if (!state.openModalsId.includes(modalId)) {
        state.openModalsId.push(modalId);
      }
    },
    removeOpenModal: (state) => {
      state.openModalsId.pop();
    },
    runTestCases: (state) => {
      state.testCasesIsLaunched = true;
    },
    stopTestCases: (state) => {
      state.testCasesIsLaunched = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setIsSandbox, (state, action) => {
      const isSandbox = action.payload;
      if (state.login && state.projectName && state.projectsSettings[state.login][state.projectName]) {
        if (isSandbox) {
          state.projectsSettings[state.login][state.projectName].testrailEnable = false;
        }
      }
    });
  },
});

export const {
  setMethods,
  setLogin,
  setProjectName,
  setSelectedEnvironment,
  setStatusCode,
  setUpdateModal,
  setTestrailEnable,
  setSelectedTab,
  setShowSpinner,
  setSelectedVariables,
  showErrorModal,
  hideErrorModal,
  addOpenModal,
  removeOpenModal,
  runTestCases,
  stopTestCases
} = mainSlice.actions;

export default mainSlice.reducer;